import { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import { ClipboardList, Map, Archive, RotateCcw } from 'lucide-react';
import { APP_ROUTES } from '../../utils/auth';
import { clearStoredProfileSession, getStoredProfile, getProfileForRole, setStoredProfile, setStoredDriver } from '../../utils/session';
import { subscribePush } from '../../utils/webpush';
import Sidebar from '../../components/Sidebar';

const Commandes     = lazy(() => import('../Commandes'));
const Confirmations = lazy(() => import('../Confirmations'));
const Expedition    = lazy(() => import('../Expedition'));
const GPS           = lazy(() => import('../GPS'));
const AllOrders     = lazy(() => import('../AllOrders'));
const VersementsPage = lazy(() => import('../Versements'));
const Traitees      = lazy(() => import('../Traitees'));
const Retours       = lazy(() => import('../Retours'));
const Reglement     = lazy(() => import('../Reglement'));

const PageSkeleton = () => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
    {[0, 1, 2, 3].map(i => (
      <div key={i} style={{ height: '72px', background: 'white', borderRadius: '14px', border: '1px solid #eef0f6', opacity: 0.35 + i * 0.18 }} />
    ))}
  </div>
);

export default function GestionnaireApp() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const RESTRICTED_TABS = ['bilans', 'presences'];
  const [activeTab, setActiveTab] = useState(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash) {
      window.history.replaceState(null, '', window.location.pathname);
      return RESTRICTED_TABS.includes(hash) ? 'commandes' : hash;
    }
    const stored = localStorage.getItem('wakaana_gest_tab') || 'commandes';
    return RESTRICTED_TABS.includes(stored) ? 'commandes' : stored;
  });
  const [pendingCount, setPendingCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);
  const [pendingLoading, setPendingLoading] = useState(true);
  const [contentVisible, setContentVisible] = useState(true);
  const [driverProfile, setDriverProfile] = useState(null); // profil livreur lié

  useEffect(() => {
    let p = getStoredProfile();
    if (!p) p = getProfileForRole('gestionnaire');
    if (!p || p.role !== 'gestionnaire') { navigate('/'); return; }
    setStoredProfile(p);
    setProfile(p);
    subscribePush(supabase, { role: 'gestionnaire' });
    // Vérifier si ce gestionnaire a aussi un profil livreur (même numéro)
    if (p.phone) {
      supabase.from('profiles').select('*').eq('phone', p.phone).eq('role', 'driver').maybeSingle()
        .then(({ data }) => { if (data) setDriverProfile(data); });
    }
  }, [navigate]);

  const fetchPending = useCallback(async () => {
    const [pendingRes, failedRes] = await Promise.all([
      supabase.from('deliveries').select('id', { count: 'exact', head: true }).eq('confirmation_status', 'new'),
      supabase.from('deliveries').select('id', { count: 'exact', head: true }).eq('status', 'failed').neq('confirmation_status', 'cancelled').not('expedition', 'eq', true),
    ]);
    if (!pendingRes.error) setPendingCount(pendingRes.count || 0);
    if (!failedRes.error) setFailedCount(failedRes.count || 0);
    setPendingLoading(false);
  }, []);

  useEffect(() => {
    let ch = null;
    let refreshTimer = null;
    fetchPending();
    ch = supabase
      .channel('realtime:gest-pending')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'deliveries' }, () => {
        if (refreshTimer) clearTimeout(refreshTimer);
        refreshTimer = setTimeout(() => fetchPending(), 350);
      })
      .subscribe();
    return () => {
      if (refreshTimer) clearTimeout(refreshTimer);
      if (ch) supabase.removeChannel(ch);
    };
  }, [fetchPending]);

  useEffect(() => {
    setContentVisible(false);
    const id = requestAnimationFrame(() => setContentVisible(true));
    return () => cancelAnimationFrame(id);
  }, [activeTab]);

  const [fromCommandes, setFromCommandes] = useState(false);

  const handleTabChange = (tab) => {
    if (tab !== 'livraisons') setFromCommandes(false);
    setActiveTab(tab);
    localStorage.setItem('wakaana_gest_tab', tab);
  };

  const handleLogout = () => {
    clearStoredProfileSession();
    localStorage.removeItem('wakaana_gest_tab');
    navigate(APP_ROUTES.landing);
  };

  if (!profile) {
    return (
      <div style={{ minHeight: '100vh', background: '#f0f3f8', padding: '20px' }}>
        <div style={{ height: '56px', borderRadius: '12px', background: '#e2e8f0', marginBottom: '12px', opacity: 0.7 }} />
        <div style={{ height: '140px', borderRadius: '14px', background: '#e2e8f0', marginBottom: '10px', opacity: 0.6 }} />
        <div style={{ height: '140px', borderRadius: '14px', background: '#e2e8f0', marginBottom: '10px', opacity: 0.5 }} />
      </div>
    );
  }

  const initials = (profile.name || 'G').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();

  const bottomTabs = [
    { id: 'commandes', label: 'Commandes', Icon: ClipboardList, badge: pendingLoading ? 0 : pendingCount },
    { id: 'carte',     label: 'Carte',     Icon: Map },
    { id: 'traitees',  label: 'Traitées',  Icon: Archive },
    { id: 'retours',   label: 'Retours',   Icon: RotateCcw, badge: failedCount },
  ];

  return (
    <div style={{ display: 'flex', height: '100dvh', overflow: 'hidden', background: '#f0f3f8' }}>
      <Sidebar
        activeNav={activeTab}
        onNavigate={handleTabChange}
        onLogout={handleLogout}
        pendingCount={pendingCount}
        failedCount={failedCount}
        role="gestionnaire"
        userName={profile.name}
        userInitials={initials}
        onSwitchToDriver={driverProfile ? async () => {
          // Charger la fiche driver liée au profil livreur
          if (driverProfile.driver_id) {
            const { data: drv } = await supabase.from('drivers').select('*').eq('id', driverProfile.driver_id).single();
            if (drv) setStoredDriver(drv);
          }
          setStoredProfile(driverProfile);
          navigate(APP_ROUTES.driver);
        } : undefined}
      />

      <main style={{ flex: 1, padding: '16px', marginTop: '64px', maxWidth: '100vw', overflowX: 'hidden', overflowY: 'auto' }}
        className="md-main admin-mobile-pad">
        <div style={{ opacity: contentVisible ? 1 : 0, transition: 'opacity 0.16s ease' }}>
          <Suspense fallback={<PageSkeleton />}>
            {activeTab === 'commandes'     && <Commandes onShowAllOrders={() => { setFromCommandes(true); handleTabChange('livraisons'); }} onNavigateTo={handleTabChange} />}
            {activeTab === 'confirmations' && <Confirmations onBack={() => handleTabChange('commandes')} />}
            {activeTab === 'livraisons'    && <AllOrders onBack={fromCommandes ? () => { setFromCommandes(false); handleTabChange('commandes'); } : null} />}
            {activeTab === 'carte'      && <GPS />}
            {activeTab === 'versements' && <VersementsPage />}
            {activeTab === 'traitees'   && <Traitees onBack={() => handleTabChange('commandes')} />}
            {activeTab === 'retours'    && <Retours onBack={() => handleTabChange('commandes')} />}
            {activeTab === 'reglement'  && <Reglement role="gestionnaire" />}
            {activeTab === 'expedition' && <Expedition onBack={() => handleTabChange('commandes')} />}
          </Suspense>
        </div>
      </main>

      {/* Bottom nav mobile */}
      <div className="admin-bottom-nav" style={{
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 999,
        background: 'white',
        borderTop: '1px solid #eef0f6',
        boxShadow: '0 -4px 24px rgba(19,40,72,0.08)',
        display: 'flex', padding: '0 4px',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}>
        {bottomTabs.map(({ id, label, Icon, badge }) => {
          const active = activeTab === id;
          return (
            <button key={id} onClick={() => handleTabChange(id)} style={{
              flex: 1, padding: '10px 4px 8px', border: 'none', background: 'none', cursor: 'pointer',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '3px',
              position: 'relative', transition: 'all 0.15s',
            }}>
              {active && (
                <div style={{ position: 'absolute', top: 0, left: '20%', right: '20%', height: '2px', borderRadius: '0 0 3px 3px', background: 'linear-gradient(90deg, #173b78, #2a66b8)' }} />
              )}
              <div style={{
                width: '34px', height: '34px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: active ? 'linear-gradient(135deg, #173b78, #2a66b8)' : 'transparent',
                transition: 'all 0.15s',
                boxShadow: active ? '0 4px 12px rgba(23,59,120,0.25)' : 'none',
              }}>
                <Icon size={16} color={active ? 'white' : '#94a3b8'} />
              </div>
              <span style={{ fontSize: '10px', fontWeight: active ? 700 : 500, color: active ? '#173b78' : '#94a3b8', transition: 'all 0.15s' }}>
                {label}
              </span>
              {badge > 0 && (
                <span style={{ position: 'absolute', top: '6px', right: 'calc(50% - 22px)', minWidth: '16px', height: '16px', borderRadius: '8px', background: '#c53a2d', color: 'white', fontSize: '9px', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px', boxShadow: '0 2px 6px rgba(197,58,45,0.4)' }}>
                  {badge}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
